import { useState, useCallback, useEffect } from "react";
import useFilters from "hooks/useFilters";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL } from "constants";

const useIterations = (projectId = null) => {

  const [state, setState] = useState({
    iterations: [],
    iterationsByProject: {}, // Map of projectId -> iterations[]
    loading: false,
    error: null,
  });

  const { selectedTagIds } = useFilters();

  const normalizePageData = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.content)) return data.content;
    return [];
  };

  /** Generic GET helper **/
  const getRequest = useCallback(async (url) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[useIterations:getRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic POST helper **/
  const postRequest = useCallback(async (url, payload) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.post(url, payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[useIterations:postRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic PUT helper **/
  const putRequest = useCallback(async (url, payload) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await apiClient.put(url, payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[useIterations:putRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Generic DELETE helper **/
  const deleteRequest = useCallback(async (url) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await apiClient.delete(url);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      console.error("[useIterations:deleteRequest]", errorMessage);
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }));
      throw new Error(errorMessage);
    }
  }, []);

  /** Fetch iterations for a specific project **/
  const fetchIterationsByProject = useCallback(async (projectIdToFetch) => {
    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectIdToFetch}/iterations`;
      const data = await getRequest(url);
      const iterationsList = normalizePageData(data);
      
      setState((prev) => ({
        ...prev,
        iterationsByProject: {
          ...prev.iterationsByProject,
          [projectIdToFetch]: iterationsList,
        },
        loading: false,
      }));
      
      return iterationsList;
    } catch (err) {
      console.error("[useIterations:fetchIterationsByProject]", err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Failed to fetch iterations",
      }));
      throw err;
    }
  }, [getRequest]);

  /** Fetch iterations for all projects (used for menu) **/
  const fetchAllIterations = useCallback(async (projectIds) => {
    if (!projectIds || projectIds.length === 0) {
      return {};
    }

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      
      // Fetch iterations for all projects in parallel
      const promises = projectIds.map((pid) => 
        apiClient.get(`${PM_SERVICE_BASE_URL}/api/v1/projects/${pid}/iterations`)
          .then(res => ({ projectId: pid, iterations: normalizePageData(res.data) }))
          .catch(err => {
            console.error(`[useIterations] Failed to fetch iterations for project ${pid}:`, err);
            return { projectId: pid, iterations: [] };
          })
      );

      const results = await Promise.all(promises);
      
      // Build the map
      const iterationsMap = {};
      results.forEach(({ projectId, iterations }) => {
        iterationsMap[projectId] = iterations;
      });

      setState((prev) => ({
        ...prev,
        iterationsByProject: iterationsMap,
        loading: false,
      }));

      return iterationsMap;
    } catch (err) {
      console.error("[useIterations:fetchAllIterations]", err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Failed to fetch iterations",
      }));
      throw err;
    }
  }, []);

  /** Create iteration **/
  const createIteration = useCallback(async (projectId, iterationData) => {
    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/iterations`;
      const data = await postRequest(url, iterationData);
      
      // Update the iterations for this project
      setState((prev) => ({
        ...prev,
        iterationsByProject: {
          ...prev.iterationsByProject,
          [projectId]: [...(prev.iterationsByProject[projectId] || []), data],
        },
        loading: false,
      }));
      
      return data;
    } catch (err) {
      console.error("[useIterations:createIteration]", err);
      throw err;
    }
  }, [postRequest]);

  /** Update iteration **/
  const updateIteration = useCallback(async (projectId, iterationId, iterationData) => {
    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/iterations/${iterationId}`;
      const data = await putRequest(url, iterationData);
      
      // Update the iteration in the map
      setState((prev) => ({
        ...prev,
        iterationsByProject: {
          ...prev.iterationsByProject,
          [projectId]: (prev.iterationsByProject[projectId] || []).map(
            (iter) => iter.id === iterationId ? data : iter
          ),
        },
        loading: false,
      }));
      
      return data;
    } catch (err) {
      console.error("[useIterations:updateIteration]", err);
      throw err;
    }
  }, [putRequest]);

  /** Delete iteration **/
  const deleteIteration = useCallback(async (projectId, iterationId) => {
    console.log("🗑️ useIterations: Deleting iteration", { projectId, iterationId });

    try {
      const url = `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/iterations/${iterationId}`;
      await deleteRequest(url);

      // Remove the iteration from the map
      setState((prev) => {
        const updatedIterationsByProject = {
          ...prev.iterationsByProject,
          [projectId]: (prev.iterationsByProject[projectId] || []).filter(
            (iter) => iter.id !== iterationId
          ),
        };

        console.log("🔄 useIterations: State updated after deletion", {
          projectId,
          iterationId,
          remainingIterations: updatedIterationsByProject[projectId]?.length || 0
        });

        return {
          ...prev,
          iterationsByProject: updatedIterationsByProject,
          loading: false,
        };
      });
    } catch (err) {
      console.error("[useIterations:deleteIteration]", err);
      throw err;
    }
  }, [deleteRequest]);

  // If projectId is provided, fetch iterations for that project on mount
  useEffect(() => {
    if (projectId) {
      fetchIterationsByProject(projectId);
    }
  }, [projectId, fetchIterationsByProject]);

  return {
    iterations: ReactifyFilter(projectId ? (state.iterationsByProject[projectId] || []) : [], selectedTagIds),
    iterationsByProject: mapFilterIterations(state.iterationsByProject, selectedTagIds),
    loading: state.loading,
    error: state.error,
    fetchIterationsByProject,
    fetchAllIterations,
    createIteration,
    updateIteration,
    deleteIteration,
  };
};

export default useIterations;

function ReactifyFilter(items = [], selectedTagIds = []) {
  if (!selectedTagIds || selectedTagIds.length === 0) return items;
  const setIds = new Set(selectedTagIds);
  return items.filter((it) => {
    const tags = it.tags || [];
    const tagIds = tags.map((t) => (typeof t === "string" ? t : t.id)).filter(Boolean);
    return tagIds.some((id) => setIds.has(id));
  });
}

function mapFilterIterations(map = {}, selectedTagIds = []) {
  if (!selectedTagIds || selectedTagIds.length === 0) return map;
  const out = {};
  Object.keys(map).forEach((k) => {
    out[k] = ReactifyFilter(map[k] || [], selectedTagIds);
  });
  return out;
}
